import {
  Component,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { IExpertCallAction } from '@techspert-io/call-actions';
import { ExpertsService, ExpertUpdateType } from '@techspert-io/experts';
import { IOpportunity } from '@techspert-io/opportunities';
import { SharedExpertEmailsService } from '@techspert-io/shared-expert-emails';
import * as moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of, timer } from 'rxjs';
import {
  catchError,
  finalize,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';
import {
  IExpert,
  IExpertAvailability,
  IExpertAvailabilityFormatted,
} from '../../../../shared/models/expert.interface';
import { TIME_ZONE } from '../../../../shared/pipes/availability-date.pipe';
import { RequestEngagementService } from '../../../../shared/services/request-engagement.service';
import { ExpertStoreService } from '../../../../shared/state/expert.service';
import { AuthDialogInceptorService } from '../../services/auth-dialog-inceptor.service';
import { AcceptanceWithdrawalBlockedDialogComponent } from './dialogs/acceptance-withdrawal-blocked-dialog/acceptance-withdrawal-blocked-dialog.component';
import { ExpertNoShowDialogComponent } from './dialogs/expert-no-show-dialog/expert-no-show-dialog.component';
import { OnHoldReasonDialogComponent } from './dialogs/reason-for-on-hold/on-hold-reason-dialog.component';
import { ReasonRejectionDialogComponent } from './dialogs/reason-for-rejection/rejection-reason-dialog.component';
import {
  IRequestTimeChangeCloseData,
  IRequestTimeChangeData,
  RequestTimeChangeComponent,
} from './dialogs/request-time-change/request-time-change.component';
import {
  ISelectAvailabilityDialogData,
  SelectAvailabilityDialogComponent,
} from './dialogs/select-availability-dialog/select-availability-dialog.component';
import { ShareExpertDialogComponent } from './dialogs/share-expert/share-expert-dialog.component';

type OnHoldRes = {
  onHoldReason: string;
  onHold: boolean;
};

type RejectionRes = {
  rejectionReason: string;
  rejected: boolean;
};

@Component({
  selector: 'app-expert',
  templateUrl: './expert.component.html',
  styleUrls: ['./expert.component.scss'],
})
export class ExpertComponent implements OnInit, OnChanges {
  @Input() expertData: IExpert;
  @Input() opportunity: IOpportunity;
  @Input() viewStage: string;
  @Input() isFileDownloadAuthorised: boolean;
  @Input() isExpanded: boolean;
  @Input() isExpertApprovalRequired: boolean;

  @ViewChild('clientNotes') clientNotes: NgModel;
  @ViewChild('expertTabGroup', { static: false }) expertTabGroup: MatTabGroup;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.smallScreen = window.innerWidth <= 1280;
  }

  expertAvailability: IExpertAvailabilityFormatted[];
  confirmClicked: boolean;
  smallScreen: boolean;
  requestNewEngagementSent: boolean;
  expertFilePrefix: string = '';
  isCallInProgress: boolean = false;
  expertIcon: string = 'new';
  isAcceptAvailabilityInProgress = false;

  unitsMapping: Record<string, string> = {
    '=1': '1 unit',
    other: '# units',
  };

  constructor(
    private dialog: MatDialog,
    private requestEngagementService: RequestEngagementService,
    private authDialogInceptorService: AuthDialogInceptorService,
    private expertStoreService: ExpertStoreService,
    private expertsService: ExpertsService,
    @Inject(TIME_ZONE) private timezone: string,
    private toastService: ToastrService,
    private shareExpertActionsService: SharedExpertEmailsService
  ) {}

  ngOnInit(): void {
    this.onResize();
    this.constructAvailability();
    this.expertFilePrefix = this.constructExpertFilePrefix();
    this.setupIcon();
  }

  ngOnChanges(sc: SimpleChanges): void {
    if (sc.isExpanded?.currentValue) {
      setTimeout(() => {
        this.expertTabGroup.realignInkBar();
      }, 500);
    }

    if (
      sc.expertData?.previousValue?.clientApproved !==
      sc.expertData?.currentValue?.clientApproved
    ) {
      this.setupIcon();
    }

    this.checkCurrentAction();
  }

  requestExpertEngagement(expert: IExpert): void {
    this.requestEngagementService
      .request(expert.expertId, 'opportunity_page')
      .pipe(tap(() => (this.requestNewEngagementSent = true)))
      .subscribe();
  }

  shareExpert(expert: IExpert): void {
    this.authDialogInceptorService.authIntercept(
      this.isFileDownloadAuthorised,
      () => this.shareExpertImpl(expert)
    );
  }

  private shareExpertImpl(expert: IExpert) {
    interface ISharedExpertDialogRes {
      recipients: string[];
      notes: string;
    }

    this.dialog
      .open<ShareExpertDialogComponent, undefined, ISharedExpertDialogRes>(
        ShareExpertDialogComponent,
        { width: '520px', autoFocus: false }
      )
      .afterClosed()
      .pipe(
        takeWhile((v) => !!v),
        switchMap((res) =>
          this.shareExpertActionsService.share(expert.expertId, res)
        ),
        tap(() => this.toastService.success('Expert successfully shared'))
      )
      .subscribe();
  }

  expertNoShow(expert: IExpert): void {
    this.dialog.open<ExpertNoShowDialogComponent, { expert: IExpert }, string>(
      ExpertNoShowDialogComponent,
      {
        width: '520px',
        height: '420px',
        data: { expert },
        autoFocus: false,
      }
    );
  }

  saveClientNotes(clientNotes: string): void {
    this.expertStoreService.updateExpert({
      expertId: this.expertData.expertId,
      type: ExpertUpdateType.UpdateClientNotes,
      payload: { clientNotes },
    });
    this.clientNotes.reset(clientNotes);
  }

  toggleRejectExpert(clientRejected: boolean, expertId: string): void {
    if (clientRejected) {
      this.dialog
        .open<ReasonRejectionDialogComponent, unknown, RejectionRes>(
          ReasonRejectionDialogComponent,
          { width: '500px', autoFocus: false }
        )
        .afterClosed()
        .pipe(takeWhile((v) => !!v))
        .subscribe(({ rejectionReason, rejected }) => {
          this.expertStoreService.updateExpert({
            expertId,
            type: ExpertUpdateType.ExpertRejection,
            payload: {
              clientRejected: rejected,
              ...(rejectionReason ? { rejectionReason } : {}),
            },
          });
        });
    } else {
      this.expertStoreService.updateExpert({
        expertId,
        type: ExpertUpdateType.ExpertRejection,
        payload: { clientRejected },
      });
    }
  }

  toggleOnHold(onHold: boolean, expertId: string): void {
    if (onHold) {
      this.dialog
        .open<OnHoldReasonDialogComponent, unknown, OnHoldRes>(
          OnHoldReasonDialogComponent,
          { width: '500px', autoFocus: false }
        )
        .afterClosed()
        .pipe(takeWhile((v) => !!v))
        .subscribe(({ onHoldReason, onHold }) => {
          this.expertStoreService.updateExpert({
            payload: { onHold, ...(onHoldReason ? { onHoldReason } : {}) },
            type: ExpertUpdateType.ExpertOnHold,
            expertId,
          });
        });
    } else {
      this.expertStoreService.updateExpert({
        payload: { onHold },
        type: ExpertUpdateType.ExpertOnHold,
        expertId,
      });
    }
  }

  toggleApproval(expert: IExpert): void {
    if (expert.clientApproved && this.viewStage !== 'new') {
      this.dialog.open<AcceptanceWithdrawalBlockedDialogComponent>(
        AcceptanceWithdrawalBlockedDialogComponent,
        {
          width: '450px',
          height: '220px',
        }
      );
      return;
    }

    this.expertStoreService.updateExpert({
      expertId: expert.expertId,
      type: ExpertUpdateType.ExpertApproval,
      payload: { clientApproved: !expert.clientApproved },
    });
  }

  acceptAvailability(availability: string): void {
    if (this.isAcceptAvailabilityInProgress) {
      return;
    }

    if (this.isPastDate(availability)) {
      return;
    }

    if (this.isExpertApprovalRequired && !this.expertData.clientApproved) {
      this.toastService.warning(
        'This expert needs approval before you are able to schedule a call',
        'Compliance approval required'
      );
      return;
    }

    this.isAcceptAvailabilityInProgress = true;

    this.expertsService
      .isExpertReadyToAccept(this.expertData.expertId)
      .pipe(
        mergeMap((readyToAccept) =>
          this.dialog
            .open<
              SelectAvailabilityDialogComponent,
              ISelectAvailabilityDialogData,
              string
            >(SelectAvailabilityDialogComponent, {
              width: '450px',
              data: { availability, readyToAccept },
              autoFocus: false,
            })
            .afterClosed()
            .pipe(
              takeWhile((outcome) => !!outcome),
              tap(() => {
                this.expertStoreService.updateExpert({
                  expertId: this.expertData.expertId,
                  payload: {
                    callTime: availability,
                    clientTimezone: moment.tz.guess(),
                  },
                  type: ExpertUpdateType.ConfirmAvailability,
                });
              })
            )
        ),
        catchError((err) => {
          console.error(err);
          this.toastService.error(
            'Please try again later or contact your Project Manager if the issue persists',
            'Failed to accept availability'
          );
          return EMPTY;
        }),
        finalize(() => (this.isAcceptAvailabilityInProgress = false))
      )
      .subscribe();
  }

  requestDifferentTime(expert: IExpert): void {
    if (this.isExpertApprovalRequired && !this.expertData.clientApproved) {
      this.toastService.warning(
        'This expert needs approval before you are able to request a consultation',
        'Compliance approval required'
      );
      return;
    }

    this.dialog
      .open<
        RequestTimeChangeComponent,
        IRequestTimeChangeData,
        IRequestTimeChangeCloseData
      >(RequestTimeChangeComponent, {
        maxHeight: '90vh',
        data: {
          expert,
          slotLength: this.opportunity.anticipatedCallTime || 60,
          slotGap: 30,
        },
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        takeWhile((request) => !!request),
        tap((request) => {
          this.expertStoreService.updateExpert({
            expertId: expert.expertId,
            type: ExpertUpdateType.RequestDifferentTime,
            payload: {
              clientTimezone: request.clientTimezone,
              newTime: request.notes,
              timeSlots: request.slots || [],
            },
          });
        }),
        catchError(() => {
          this.dialog.open(RequestTimeChangeComponent, {
            width: '360px',
            height: '378px',
            data: {
              expert: expert,
              error: 'Failed to submit request - please retry',
            },
          });
          return of(undefined);
        })
      )
      .subscribe();
  }

  isPastDate(date: string): boolean {
    return new Date(date).getTime() < new Date().getTime();
  }

  checkCurrentAction(): void {
    const currentAction = this.getCurrentAction();
    const upcomingAction = this.getUpcomingAction();

    const now = new Date().getTime();
    if (currentAction) {
      this.isCallInProgress = true;

      timer(this.getActionEndTime(currentAction) - now).subscribe(
        () => (this.isCallInProgress = false)
      );
    } else if (upcomingAction) {
      timer(this.getActionStartTime(upcomingAction) - now).subscribe(
        () => (this.isCallInProgress = true)
      );

      timer(this.getActionEndTime(upcomingAction) - now).subscribe(
        () => (this.isCallInProgress = false)
      );
    }
  }

  onAuthTabClick(): void {
    this.authDialogInceptorService.authIntercept(this.isFileDownloadAuthorised);
  }

  private constructExpertFilePrefix(): string {
    return `${this.expertData.firstName.charAt(
      0
    )}${this.expertData.lastName.charAt(0)}`;
  }

  private setupIcon(): void {
    this.expertIcon =
      this.viewStage === 'new' &&
      this.isExpertApprovalRequired &&
      !this.expertData.clientApproved
        ? 'requires-approval'
        : this.viewStage;
  }

  private getUpcomingAction(): IExpertCallAction {
    const now = new Date().getTime();
    return (this.expertData.actions || []).find(
      (action) =>
        action.actionType === 'call' &&
        new Date(action.datetime).getTime() > now
    ) as IExpertCallAction;
  }

  private getCurrentAction(): IExpertCallAction {
    const now = new Date().getTime();
    return (this.expertData.actions || []).find(
      (action) =>
        action.actionType === 'call' &&
        now > this.getActionStartTime(action) &&
        now < this.getActionEndTime(action)
    ) as IExpertCallAction;
  }

  private getActionStartTime(action: IExpertCallAction): number {
    return new Date(action.datetime).getTime();
  }

  private getActionEndTime(action: IExpertCallAction): number {
    return this.getActionStartTime(action) + action.duration * 60 * 1000;
  }

  private constructAvailability(): void {
    const timeSlots = (this.expertData.actions || []).flatMap((d) =>
      d.actionType === 'availabilities' && d.slotRequestType === 'expert'
        ? d.timeSlots
        : []
    );
    this.expertAvailability = this.formatAvailabilities(
      timeSlots.length ? timeSlots : this.expertData.availabilities
    );
  }

  private formatAvailabilities(
    availabilities: IExpertAvailability[]
  ): IExpertAvailabilityFormatted[] {
    return availabilities
      .map((obj) => ({
        ...obj,
        start: moment(obj.start).tz(this.timezone).format(),
        date: moment(obj.start).tz(this.timezone).format('YYYY-MM-DD'),
      }))
      .reduce((acc, curr) => {
        acc.some((object) => object.date === curr.date)
          ? acc
              .filter((object) => object.date === curr.date)[0]
              .times.push(curr.start)
          : acc.push({ date: curr.date, times: [curr.start] });
        return acc;
      }, []);
  }
}
